/**
 * Страница массового мероприятия
 * Показывает детали batch события и таблицу дочерних событий
 */
/** @jsxImportSource @emotion/react */
import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Card, Button, Space, Typography, Alert, Spin, Breadcrumb } from 'antd';
import { ArrowLeftOutlined, ReloadOutlined } from '@ant-design/icons';
import { css } from '@emotion/react';
import { commonStyles } from '../../styles/commonStyles';
import { useGetBatchChildrenQuery } from '../../api/plantingEventsApi';
import { BatchChildEvent } from '../../types/plantingEvents';
import { CompleteEventModal } from '../../components/events/CompleteEventModal';
import { BulkCompleteEventsModal } from '../../components/events/BulkCompleteEventsModal';
import { BatchEventTable } from '../../components/events/components/BatchEventTable';
import { EventProgressBar } from '../../components/events/components/EventProgressBar';
import { hasAction } from '../../types/permissions';
import { useSave } from '../../contexts/WorldContext';
import { useTranslation } from 'react-i18next';

const { Title } = Typography;

const styles = {
  breadcrumbSpacing: css({ marginBottom: 16 }),
  cardSpacing: css({ marginBottom: 16 }),
  backButton: css({ marginTop: 16 }),
};

/**
 * Отдельная страница для просмотра массового мероприятия
 * Заменяет модалку BatchEventDetailsModal (без проблемы "модалка над модалкой")
 */
export const BatchEventPage: React.FC = () => {
  const { t } = useTranslation('events');
  const { saveId, eventId } = useParams<{ saveId: string; eventId: string }>();
  const save = useSave();
  const farmId = save.farmId || '';
  const navigate = useNavigate();
  const [selectedChild, setSelectedChild] = useState<BatchChildEvent | null>(null);
  const [selectedEventIds, setSelectedEventIds] = useState<string[]>([]);
  const [bulkCompleteModalOpen, setBulkCompleteModalOpen] = useState(false);

  const farmIdValue = farmId;
  const eventIdValue = eventId || '';

  const user = useSelector((state: any) => state.auth.user);

  // Права на выполнение событий
  const canCompleteEvents =
    user?.role === 'superuser' ||
    user?.role === 'agronomist' ||
    hasAction(user, 'events', 'events.complete', farmIdValue) ||
    hasAction(user, 'events', 'events.update', farmIdValue);

  const {
    data: batchData,
    isLoading,
    error,
    refetch,
  } = useGetBatchChildrenQuery(
    { parentEventId: eventIdValue, saveId: saveId || '' },
    { skip: !saveId || !eventIdValue }
  );

  const handleCompleteEvent = (event: BatchChildEvent) => {
    setSelectedChild(event);
  };

  const handleCompleteSuccess = () => {
    refetch();
    setSelectedChild(null);
  };

  const handleBulkComplete = () => {
    if (selectedEventIds.length === 0) {
      return;
    }
    setBulkCompleteModalOpen(true);
  };

  const handleBulkCompleteSuccess = () => {
    setSelectedEventIds([]);
    setBulkCompleteModalOpen(false);
    refetch();
  };

  const selectedEvents = batchData?.children?.filter((child) =>
    selectedEventIds.includes(String(child.id))
  ) || [];

  if (isLoading) {
    return (
      <div css={css([commonStyles.textCenter, { padding: '100px' }])}>
        <Spin size="large" />
      </div>
    );
  }

  if (error || !batchData) {
    return (
      <Card>
        <Alert
          message={t('batch.loadErrorTitle')}
          description={t('batch.loadErrorDescription')}
          type="error"
          showIcon
        />
        <Button
          type="primary"
          onClick={() => navigate(`/saves/${saveId}/tasks`)}
          css={styles.backButton}
        >
          {t('batch.returnToTasks')}
        </Button>
      </Card>
    );
  }

  return (
    <div>
      {/* Breadcrumbs */}
      <Breadcrumb css={styles.breadcrumbSpacing}>
        <Breadcrumb.Item>
          <Link to={`/saves/${saveId}`}>{t('batch.breadcrumbSave')}</Link>
        </Breadcrumb.Item>
        <Breadcrumb.Item>
          <Link to={`/saves/${saveId}/tasks`}>{t('batch.breadcrumbTasks')}</Link>
        </Breadcrumb.Item>
        <Breadcrumb.Item>{t('batch.breadcrumbEvent', { id: eventId })}</Breadcrumb.Item>
      </Breadcrumb>

      {/* Header */}
      <Card css={styles.cardSpacing}>
        <Space direction="vertical" size="middle" css={commonStyles.fullWidth}>
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(`/saves/${saveId}/tasks`)}>
              {t('batch.backToTasks')}
            </Button>
            <Button icon={<ReloadOutlined />} onClick={() => refetch()}>
              {t('common:actions.refresh')}
            </Button>
          </Space>

          <Title level={3}>
            {t('batch.title', { name: batchData.parent?.type || t('batch.untitled') })}
          </Title>

          {batchData.parent && (
            <EventProgressBar
              completed={batchData.parent.completedCount || 0}
              total={batchData.children?.length || 0}
              showText={true}
            />
          )}
        </Space>
      </Card>

      {/* Таблица дочерних событий */}
      <Card
        title={t('batch.taskList')}
        extra={
          selectedEventIds.length > 0 && (
            <Space>
              <Button type="primary" onClick={handleBulkComplete}>
                {t('batch.bulkComplete', { count: selectedEventIds.length })}
              </Button>
              <Button onClick={() => setSelectedEventIds([])}>
                {t('batch.clearSelection')}
              </Button>
            </Space>
          )
        }
      >
        <BatchEventTable
          events={batchData.children || []}
          loading={false}
          onCompleteEvent={handleCompleteEvent}
          canCompleteEvents={canCompleteEvents}
          selectedEventIds={selectedEventIds}
          onSelectionChange={setSelectedEventIds}
        />
      </Card>

      {/* Модалка завершения дочернего события */}
      {selectedChild && batchData?.parent && (
        <CompleteEventModal
          open={!!selectedChild}
          event={{
            id: selectedChild.id,
            type: selectedChild.type || batchData.parent.type,
            status: selectedChild.status,
            plantingId: selectedChild.plantingId?.toString() || '',
            plantingType: 'single',
            plantingQuantity: 1,
            cropId: selectedChild.cropId || 0,
            cropName: selectedChild.cropName || '',
            cropLatinName: selectedChild.cropLatinName || '',
            plannedDate: selectedChild.plannedDate || '',
            actualDate: selectedChild.actualDate,
            notes: selectedChild.notes,
            templateId: selectedChild.templateId || batchData.parent.templateId,
            templateName: selectedChild.templateName || batchData.parent.type,
            properties: selectedChild.properties || [],
          }}
          farmId={farmId || ''}
          onClose={() => setSelectedChild(null)}
          onSuccess={handleCompleteSuccess}
        />
      )}

      {/* Модалка группового выполнения */}
      {bulkCompleteModalOpen && batchData?.parent && selectedEvents.length > 0 && (
        <BulkCompleteEventsModal
          open={bulkCompleteModalOpen}
          events={selectedEvents}
          templateId={batchData.parent.templateId}
          templateName={batchData.parent.type}
          farmId={farmId || ''}
          onClose={() => setBulkCompleteModalOpen(false)}
          onSuccess={handleBulkCompleteSuccess}
        />
      )}
    </div>
  );
};

export default BatchEventPage;
